import type { OrderProductInfo } from "../OrderProductInfo.js";
import type {
	ProductAttributeGroupOfValue,
	ProductStockForCombination,
	ProductVariant,
	ProductVariantForCustomer,
} from "../Product.js";

/**
 * Returns the count of products in stock for given variant and attributes.
 * If there is no stock item for given variant and attributes, it means that stock is not limited, so it returns Infinity.
 */
export function getProductStockAmount(
	stock: ProductStockForCombination[] | undefined,
	variant: ProductVariantForCustomer | ProductVariant | OrderProductInfo["variant"],
	attributes: Pick<ProductAttributeGroupOfValue, "id" | "value">[],
): number {
	if (!stock || stock.length === 0) {
		return Infinity;
	}

	const stockForVariant = stock.filter((stockItem) => stockItem.variantId === variant.id);
	if (stockForVariant.length === 0) {
		return Infinity;
	}

	const stockForCombination = stockForVariant.find((stockItem) =>
		stockItem.attributes.every(({ groupId, valueId }) =>
			attributes.some((attribute) => attribute.id === groupId && attribute.value.id === valueId),
		),
	);

	if (!stockForCombination) {
		return Infinity;
	}

	return stockForCombination.amount;
}

export function getProductStockAmountForAllVariantsOfAttributes(
	stock: ProductStockForCombination[] | undefined,
	variants: ProductVariantForCustomer[] | ProductVariant[] | OrderProductInfo["variant"][],
	attributes: Pick<ProductAttributeGroupOfValue, "id" | "value">[],
): number {
	return variants.reduce((acc, variant) => (acc += getProductStockAmount(stock, variant, attributes)), 0);
}
